import { Loader2, UploadCloud, X } from 'lucide-react';
import { useState } from 'react';
import { useImageUpload } from '../../../hooks/useImageUpload';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';

interface SpaceImageUploadProps {
  imageUrl: string;
  onImageChange: (url: string) => void;
  onRemove: () => void;
}

export function SpaceImageUpload({ imageUrl, onImageChange, onRemove }: SpaceImageUploadProps) {
  const { uploadImage, createPreview, revokePreview, isUploading, error: uploadError } = useImageUpload();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const localUrl = createPreview(file);
    setPreviewUrl(localUrl);
    const result = await uploadImage(file);
    if (result.success && result.url) onImageChange(result.url);
    setPreviewUrl(null);
    revokePreview(localUrl);
  };

  const displayUrl = previewUrl || imageUrl;

  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold text-[#00303c]">Image de l'espace</Label>

      {displayUrl ? (
        <div className="relative w-full aspect-video md:aspect-[21/9] rounded-xl overflow-hidden border border-slate-200 bg-slate-50 group">
          <img
            src={displayUrl}
            alt="Aperçu de l'espace"
            className={cn(
              'w-full h-full object-cover transition-opacity duration-300',
              isUploading ? 'opacity-40' : 'opacity-100',
            )}
          />

          {isUploading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/20 backdrop-blur-[2px]">
              <Loader2 className="w-8 h-8 animate-spin text-[#8E0B56]" />
              <span className="text-xs font-medium text-slate-700">Traitement de l'image...</span>
            </div>
          )}

          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="h-8 w-8 rounded-full shadow-lg"
              onClick={onRemove}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <label
            htmlFor="space-image-upload"
            className={cn(
              'flex flex-col items-center justify-center w-full min-h-[160px] px-6 py-8 transition-all border-2 border-dashed rounded-xl cursor-pointer group',
              isUploading
                ? 'bg-slate-50 border-slate-200 cursor-not-allowed'
                : 'bg-white border-slate-200 hover:border-[#8E0B56]/40 hover:bg-slate-50/50',
            )}
          >
            <div className="flex flex-col items-center justify-center space-y-3 text-center">
              <div className="p-3 transition-colors rounded-full bg-slate-100 group-hover:bg-[#8E0B56]/10">
                <UploadCloud className="w-6 h-6 text-slate-500 group-hover:text-[#8E0B56]" />
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-700">
                  {isUploading ? 'Téléchargement...' : 'Cliquez pour sélectionner une image'}
                </p>
                <p className="text-xs text-slate-500">PNG, JPG ou WEBP jusqu'à 5 Mo</p>
              </div>
            </div>

            <input
              id="space-image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUploading}
              className="hidden"
            />
          </label>
        </div>
      )}

      {uploadError && (
        <div className="flex items-center gap-2 p-2 mt-2 text-xs font-medium text-red-600 rounded-lg bg-red-50">
          <X className="w-3.5 h-3.5" />
          {uploadError}
        </div>
      )}
    </div>
  );
}
